import { useMemo, useState, useEffect } from "react";
import { Search, X, Check } from "lucide-react";
import Dialog from "./Dialog";
import { normalizeForSearch, matchesAllTokens } from "@/lib/searchNormalize";

/**
 * Iter 117 — Buscador de inventario en modal para añadir múltiples artículos
 * a una Factura o Albarán en una sola operación.
 *
 * Props:
 *  - open           : boolean
 *  - onClose        : () => void
 *  - parts          : lista completa de productos/servicios cargados (Part[])
 *  - onAdd(picked)  : callback con array de items a añadir al documento.
 *                     Cada item respeta la relación con el ID original de
 *                     Inventario (`product_id: p.id`) para stock y trazabilidad.
 */
export default function InventorySearchDialog({ open, onClose, parts = [], onAdd }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState({}); // { [partId]: qty }

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected({});
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = normalizeForSearch(query);
    if (!q) return parts.slice(0, 80);
    return parts.filter((p) => {
      // Iter 125 — Búsqueda por tokens independientes (AND, orden libre).
      // Insensible a acentos/diéresis/mayúsculas.
      return matchesAllTokens(
        [p.name, p.sku, p.barcode, p.category],
        query,
      );
    }).slice(0, 150);
  }, [query, parts]);

  const toggle = (p) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[p.id]) delete next[p.id];
      else next[p.id] = 1;
      return next;
    });
  };

  const setQty = (id, qty) => {
    setSelected((prev) => ({ ...prev, [id]: Math.max(1, parseInt(qty || "1", 10) || 1) }));
  };

  const selectedCount = Object.keys(selected).length;

  const confirm = () => {
    const picked = [];
    parts.forEach((p) => {
      const qty = selected[p.id];
      if (!qty) return;
      picked.push({
        product_id: p.id,
        sku: p.sku || "",
        name: p.name || "",
        description: p.description || "",
        kind: p.kind || "product",
        quantity: qty,
        unit_price: Number(p.sale_price) || 0,
        vat_rate: p.vat_rate ?? 21,
        warranty_months: Number(p.warranty_months) || 0,
      });
    });
    if (picked.length > 0 && onAdd) onAdd(picked);
    onClose && onClose();
  };

  const warrantyLabel = (m) => (m === 0 ? "Sin garantía" : m === 1 ? "1 mes" : `${m} meses`);
  const kindLabel = (k) => (k === "service" ? "Servicio" : "Producto");

  if (!open) return null;

  return (
    <Dialog onClose={onClose} title="Añadir artículos del inventario" wide>
      <div className="space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, SKU, código de barras o categoría..."
            data-testid="inventory-search-input"
            className="w-full border border-[#E5E5E0] bg-white pl-9 pr-3 py-2 text-sm focus:border-[#6E8B3D] outline-none"
          />
        </div>

        <div className="border border-[#E5E5E0]" data-testid="inventory-search-results">
          <div className="max-h-[420px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-6 text-xs text-[#525252] text-center">No se han encontrado artículos.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-[#111110] text-white sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 w-8"></th>
                    <th className="px-2 py-1.5 text-left font-mono uppercase tracking-widest text-[10px]">Nombre</th>
                    <th className="px-2 py-1.5 text-left font-mono uppercase tracking-widest text-[10px]">SKU</th>
                    <th className="px-2 py-1.5 text-left font-mono uppercase tracking-widest text-[10px]">Categoría</th>
                    <th className="px-2 py-1.5 text-left font-mono uppercase tracking-widest text-[10px]">Tipo</th>
                    <th className="px-2 py-1.5 text-right font-mono uppercase tracking-widest text-[10px]">Precio</th>
                    <th className="px-2 py-1.5 text-left font-mono uppercase tracking-widest text-[10px]">Garantía</th>
                    <th className="px-2 py-1.5 text-right font-mono uppercase tracking-widest text-[10px]">Stock</th>
                    <th className="px-2 py-1.5 text-center font-mono uppercase tracking-widest text-[10px]">Uds.</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const isSelected = !!selected[p.id];
                    const isService = (p.kind || "product") === "service";
                    const stock = Number(p.stock) || 0;
                    const lowStock = !isService && stock <= (Number(p.min_stock) || 0);
                    return (
                      <tr
                        key={p.id}
                        className={`border-b border-[#E5E5E0] last:border-0 cursor-pointer hover:bg-[#F7F7F4] ${isSelected ? "bg-[#F0F4E8]" : ""}`}
                        onClick={() => toggle(p)}
                        data-testid={`inventory-search-row-${p.id}`}
                      >
                        <td className="px-2 py-2 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggle(p)}
                            data-testid={`inventory-search-check-${p.id}`}
                            className="cursor-pointer"
                          />
                        </td>
                        <td className="px-2 py-2 font-semibold">{p.name}</td>
                        <td className="px-2 py-2 font-mono text-[#525252]">{p.sku}</td>
                        <td className="px-2 py-2">{p.category || "—"}</td>
                        <td className="px-2 py-2">
                          <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${isService ? "bg-[#E9F0F7] text-[#2C5578]" : "bg-[#F0F4E8] text-[#4A6820]"}`}>
                            {kindLabel(p.kind)}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono">€{Number(p.sale_price || 0).toFixed(2)}</td>
                        <td className="px-2 py-2 text-[11px]">{warrantyLabel(Number(p.warranty_months) || 0)}</td>
                        <td className="px-2 py-2 text-right font-mono">
                          {isService ? (
                            <span className="text-[#A8A8A0]">—</span>
                          ) : (
                            <span className={lowStock ? "text-red-700 font-bold" : ""}>{stock}</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          {isSelected ? (
                            <input
                              type="number"
                              min="1"
                              value={selected[p.id]}
                              onChange={(e) => setQty(p.id, e.target.value)}
                              data-testid={`inventory-search-qty-${p.id}`}
                              className="w-14 border border-[#E5E5E0] px-1 py-0.5 text-xs text-center bg-white"
                            />
                          ) : (
                            <span className="text-[#A8A8A0]">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-[#E5E5E0]">
          <div className="text-xs text-[#525252]">
            {selectedCount === 0
              ? "Selecciona uno o varios artículos"
              : `${selectedCount} artículo${selectedCount === 1 ? "" : "s"} seleccionado${selectedCount === 1 ? "" : "s"}`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs uppercase tracking-widest text-[#525252] hover:bg-[#F7F7F4] flex items-center gap-1"
              data-testid="inventory-search-cancel"
            >
              <X size={12} /> Cancelar
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={selectedCount === 0}
              className="px-4 py-1.5 text-xs uppercase tracking-widest bg-[#6E8B3D] text-white hover:bg-[#5d7634] disabled:opacity-50 flex items-center gap-1"
              data-testid="inventory-search-confirm"
            >
              <Check size={12} /> Añadir seleccionados
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
